import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock, IndianRupee, TrendingUp, ShoppingCart, Plus } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    // Update cart count on mount and when localStorage changes
    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    window.addEventListener('cartUpdated', updateCartCount);
    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, []);

  const updateCartCount = () => {
    const cart = JSON.parse(localStorage.getItem('serviceCart') || '[]');
    setCartCount(cart.length);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" data-testid="navbar-logo" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <img src="/cleo-logo.png" alt="Cleo Professionals" style={{ height: '100px', width: 'auto' }} />
        </Link>
        
        <ul className="navbar-menu">
          <li><Link to="/" className="navbar-link" data-testid="nav-home">Home</Link></li>
          <li><Link to="/services" className="navbar-link" data-testid="nav-services">Services</Link></li>
          <li><Link to="/gallery" className="navbar-link" data-testid="nav-gallery">Gallery</Link></li>
          <li><Link to="/booking" className="navbar-cta blink-animation" data-testid="nav-booking">Book Now</Link></li>
        </ul>
        
        {/* Cart with Text - Before Hamburger */}
        <Link 
          to="/booking" 
          className="cart-with-label"
          style={{ 
            position: 'relative',
            marginLeft: '2rem',
            padding: '0.5rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'linear-gradient(135deg, #d4a574, #c89b64)',
            borderRadius: '8px',
            textDecoration: 'none',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(212, 165, 116, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <span style={{ 
            color: 'white', 
            fontWeight: '600',
            fontSize: '1rem',
            letterSpacing: '0.5px'
          }} className="blink-animation">
            Cart
          </span>
          <div style={{ position: 'relative' }}>
            <ShoppingCart size={20} color="white" />
            {cartCount > 0 && (
              <span className="cart-badge-red" style={{
                background: '#ff4444',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7rem',
                fontWeight: '700',
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                border: '2px solid white'
              }}>
                {cartCount}
              </span>
            )}
          </div>
        </Link>
        
        <button 
          className={`mobile-menu-btn ${mobileMenuOpen ? 'hidden' : ''}`}
          data-testid="mobile-menu-btn" 
          onClick={toggleMobileMenu}
        >
          ☰
        </button>
      </div>
      
      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'mobile-menu-open' : ''}`} onClick={closeMobileMenu}>
        <div className="mobile-menu-content" onClick={(e) => e.stopPropagation()}>
          <div className="mobile-menu-header">
            <img src="/cleo-logo.png" alt="Cleo Professionals" style={{ height: '60px', width: 'auto' }} />
            <button className="mobile-menu-close" onClick={closeMobileMenu}>✕</button>
          </div>
          <ul className="mobile-menu-list">
            <li><Link to="/" className="mobile-menu-link" onClick={closeMobileMenu}>HOME</Link></li>
            <li><Link to="/services" className="mobile-menu-link" onClick={closeMobileMenu}>Services</Link></li>
            <li><Link to="/gallery" className="mobile-menu-link" onClick={closeMobileMenu}>Gallery</Link></li>
            <li>
              <Link to="/booking" className="mobile-menu-link" onClick={closeMobileMenu} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                <ShoppingCart size={18} className="blink-animation" />
                Cart
                {cartCount > 0 && (
                  <span className="cart-badge-red" style={{
                    background: '#ff4444',
                    color: 'white',
                    borderRadius: '50%',
                    width: '22px',
                    height: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    marginLeft: '0.25rem'
                  }}>
                    {cartCount}
                  </span>
                )}
              </Link>
            </li>
            <li><Link to="/booking" className="mobile-menu-link mobile-menu-cta blink-animation" onClick={closeMobileMenu}>Book Now</Link></li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

const Services = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [addedServices, setAddedServices] = useState(new Set());

  useEffect(() => {
    fetchServices();
    fetchCategories();
    loadCartFromStorage();
  }, []);

  const loadCartFromStorage = () => {
    const cart = JSON.parse(localStorage.getItem('serviceCart') || '[]');
    const serviceIds = new Set(cart.map(s => s.id));
    setAddedServices(serviceIds);
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/services/categories`);
      const cats = response.data.categories;
      setCategories(cats);
      // Set first category as default
      if (cats.length > 0 && !selectedCategory) {
        setSelectedCategory(cats[0]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const addToCart = (service) => {
    const cart = JSON.parse(localStorage.getItem('serviceCart') || '[]');
    
    // Check if service already in cart
    const existingIndex = cart.findIndex(s => s.id === service.id);
    if (existingIndex !== -1) {
      toast.info(`${service.name} is already in your cart`);
      return;
    }

    // Add service to cart
    cart.push(service);
    localStorage.setItem('serviceCart', JSON.stringify(cart));
    
    // Update local state
    setAddedServices(new Set([...addedServices, service.id]));
    
    // Dispatch event for navbar to update
    window.dispatchEvent(new Event('cartUpdated'));
    
    toast.success(`${service.name} added to cart!`);
  };

  const removeFromCart = (serviceId) => {
    const cart = JSON.parse(localStorage.getItem('serviceCart') || '[]');
    const updatedCart = cart.filter(s => s.id !== serviceId);
    localStorage.setItem('serviceCart', JSON.stringify(updatedCart));
    
    const newAddedServices = new Set(addedServices);
    newAddedServices.delete(serviceId);
    setAddedServices(newAddedServices);
    
    window.dispatchEvent(new Event('cartUpdated'));
    toast.success('Service removed from cart');
  };

  const filteredServices = services.filter(service => {
    return !selectedCategory || service.category === selectedCategory;
  });

  return (
    <div>
      <Navbar />
      
      {/* Header Section */}
      <section style={{
        paddingTop: '120px',
        paddingBottom: '60px',
        background: 'linear-gradient(135deg, #fef5e7 0%, #f9f0e8 100%)',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2rem' }}>
          <span className="section-badge">Our Services</span>
          <h1 className="section-title" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
            Premium Beauty Services
          </h1>
          <p className="section-subtitle">
            Discover our comprehensive range of professional beauty treatments
          </p>
        </div>
      </section>

      {/* Filters Section */}
      <section style={{ padding: '3rem 2rem', background: 'white', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Category Dropdown Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '1rem' }}>
                Filter by Category:
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                data-testid="category-dropdown"
                style={{
                  padding: '0.75rem 1.25rem',
                  borderRadius: '8px',
                  border: '2px solid #d4a574',
                  background: 'white',
                  color: '#2c3e50',
                  fontWeight: '600',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  outline: 'none',
                  minWidth: '200px',
                  transition: 'all 0.3s ease'
                }}
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Cart Icon Next to Filter */}
            <Link 
              to="/booking"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, #d4a574, #c89b64)',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                position: 'relative'
              }}
              className="blink-animation"
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(212, 165, 116, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <ShoppingCart size={20} />
              View Cart
              {addedServices.size > 0 && (
                <span className="cart-badge-red" style={{
                  background: '#ff4444',
                  color: 'white',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px'
                }}>
                  {addedServices.size}
                </span>
              )}
            </Link>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section style={{ padding: '4rem 2rem', background: '#fafafa' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '2rem'
          }}>
            <h2 style={{ fontSize: '1.5rem', color: '#2c3e50' }}>
              {selectedCategory || 'Services'}
            </h2>
            <p style={{ color: '#5a6c7d' }} data-testid="services-count">
              {filteredServices.length} service{filteredServices.length !== 1 ? 's' : ''}
            </p>
          </div>

          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          ) : filteredServices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
              <p style={{ fontSize: '1.25rem', color: '#5a6c7d' }}>No services found</p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '1.5rem'
            }}>
              {filteredServices.map((service) => {
                const isAdded = addedServices.has(service.id);
                return (
                  <div
                    key={service.id}
                    data-testid={`service-${service.id}`}
                    style={{
                      background: 'white',
                      borderRadius: '15px',
                      padding: '1.5rem',
                      border: isAdded ? '2px solid #d4a574' : '1px solid #f0f0f0',
                      transition: 'all 0.3s ease',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      if (!isAdded) {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {isAdded && (
                      <div style={{
                        position: 'absolute',
                        top: '1rem',
                        right: '1rem',
                        background: '#4ade80',
                        color: 'white',
                        padding: '0.35rem 0.75rem',
                        borderRadius: '50px',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        ✓ Added
                      </div>
                    )}
                    <div style={{ 
                      display: 'inline-block',
                      padding: '0.35rem 0.75rem',
                      background: 'rgba(212, 165, 116, 0.15)',
                      color: '#c89b64',
                      borderRadius: '50px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      marginBottom: '1rem'
                    }}>
                      {service.category}
                    </div>
                    {service.is_trending && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.35rem 0.75rem',
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        color: 'white',
                        borderRadius: '50px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        marginLeft: '0.5rem',
                        marginBottom: '1rem'
                      }}>
                        <TrendingUp size={14} />
                        Trending
                      </span>
                    )}
                    <h3 style={{ 
                      fontSize: '1.125rem', 
                      color: '#2c3e50', 
                      marginBottom: '1rem',
                      fontFamily: 'Playfair Display, serif'
                    }}>
                      {service.name}
                    </h3>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginTop: '1rem',
                      paddingTop: '1rem',
                      borderTop: '1px solid #f0f0f0'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <IndianRupee size={18} color="#d4a574" />
                        <span style={{ 
                          fontSize: '1.5rem', 
                          fontWeight: '700', 
                          color: '#2c3e50'
                        }}>
                          {service.price.toFixed(2)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#5a6c7d' }}>
                        <Clock size={16} />
                        <span style={{ fontSize: '0.875rem' }}>{service.duration} min</span>
                      </div>
                    </div>
                    
                    {/* Add to Cart Button */}
                    <button
                      onClick={() => isAdded ? removeFromCart(service.id) : addToCart(service)}
                      style={{
                        width: '100%',
                        marginTop: '1rem',
                        padding: '0.875rem',
                        borderRadius: '8px',
                        border: 'none',
                        background: isAdded 
                          ? 'linear-gradient(135deg, #ef4444, #dc2626)' 
                          : 'linear-gradient(135deg, #d4a574, #c89b64)',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '1rem',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(212, 165, 116, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {isAdded ? (
                        <>✓ Remove from Cart</>
                      ) : (
                        <>
                          <Plus size={18} />
                          Add to Cart
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '4rem 2rem',
        background: 'linear-gradient(135deg, #d4a574, #c89b64)',
        textAlign: 'center',
        color: 'white'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '1rem', color: 'white' }}>
            Ready to Book Your Service?
          </h2>
          <p style={{ fontSize: '1.125rem', marginBottom: '2rem', opacity: 0.9 }}>
            Choose your desired services and book an appointment at your convenience
          </p>
          <Link 
            to="/booking" 
            className="btn-primary"
            data-testid="services-book-btn"
            style={{ 
              background: 'white', 
              color: '#d4a574',
              boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
            }}
          >
            Book Appointment
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
